import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import StopCircleIcon from '@mui/icons-material/StopCircle'
import {
  Badge,
  Button,
  Chip,
  Divider,
  Fade,
  Paper,
  Popper,
  PopperPlacementType,
  Stack,
  Typography,
} from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import {blue, blueGrey, brown, grey, indigo, red} from '@mui/material/colors'
import {ISavedSearchFE} from 'interfaces/ISavedSearchFE'
import React from 'react'
import Skeleton from 'react-loading-skeleton'
import {useDispatch, useSelector} from 'react-redux'
import {deleteSavedSearch, updateSavedNotificationSearch} from 'redux/actions/savedSearchActions'
import {selectSavedSearchSlice} from 'redux/selectors/savedSearchSelector'
import {AppDispatch} from 'redux/store'
import ShowMoreText from './ShowMoreText'
import DragableConceptsListNotifications from './concepts/DraggableConceptListNotifications'
import ShowMoreConceptsNotify from './concepts/ShowMoreConceptsNotify'
import ConfirmationDialog from './dialogs/ConfirmationDialog'
import EmptyStateCard from './emptyStates/emptyFolder'

interface Props {
  searchResults?: ISavedSearchFE[]
  loading: boolean
  handleEditSearch?: (es: ISavedSearchFE | undefined, show: boolean | undefined) => void
}

export default function SearchResultNotify({searchResults, loading, handleEditSearch}: Props) {
  const dispatch = useDispatch<AppDispatch>()
  const savedSearchRedux = useSelector(selectSavedSearchSlice)

  //if you delete this result from the list, it could be the search that was just added from the dialog
  const handleDeleteSearchFromList = async (searchId: string) => {
    dispatch(deleteSavedSearch({id: searchId}))
    handleEditSearch && handleEditSearch(undefined, false)
  }

  const handlePauseSearchFromList = async (search: ISavedSearchFE) => {
    if (search?.id) {
      const copy_search = {...search} // create a copy to avoid mutating the original
      copy_search.is_paused = !search.is_paused // toggle pause state
      const res = await dispatch(
        updateSavedNotificationSearch({
          id: search.id,
          search: copy_search,
          originalSavedSearch: search,
        })
      )
    }
    //  close the dialog if it was open
    handleEditSearch && handleEditSearch(undefined, false)
  }

  const [removeSearch, setRemoveSearch] = React.useState<ISavedSearchFE>()
  const [deleteSearch, setDeleteSearch] = React.useState<ISavedSearchFE>()
  const [pauseSearch, setPauseSearch] = React.useState<ISavedSearchFE>()

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const [showPopper, setShowPopper] = React.useState<{
    popper_id: string
    placement: PopperPlacementType
  }>()

  const [placement, setPlacement] = React.useState<PopperPlacementType>()
  const handleOpenPopper =
    (popperid: string, newPlacement: PopperPlacementType) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!showPopper) {
        //open popper
        setAnchorEl(event.currentTarget)
        setShowPopper({popper_id: popperid, placement: newPlacement})
        setPlacement(newPlacement)
      } else {
        setShowPopper(undefined)
        setAnchorEl(null)
        //setShowPopper({popper_id: popperid, placement: newPlacement})
      }
    }

  const handleDeleteKeyword = async (search: ISavedSearchFE, keyword: string) => {
    if (!search || !search.id) {
      return
    }
    const updatedSearch = {...search} // create a copy to avoid mutating the original
    updatedSearch.keywords = search?.keywords?.filter((x) => x != keyword)
    const res = await dispatch(
      updateSavedNotificationSearch({
        id: search.id,
        search: updatedSearch,
        originalSavedSearch: search,
      })
    )
  }

  return (
    <Box sx={{flex: '1 1 auto', flexDirection: 'column'}}>
      <Box>
        {!searchResults && loading ? (
          <Grid item xs={12}>
            <Skeleton
              height={200}
              count={5}
              wrapper={({children}) => {
                return <div style={{marginBottom: '10px'}}>{children}</div>
              }}
            />
          </Grid>
        ) : (
          <>
            {searchResults?.map((search, index) => {
              return (
                <Box key={`sr_${index}`} mb={1} pb={3}>
                  <Box display={'flex'} flexDirection={'column'} mt={1}>
                    <Box display={'flex'}>
                      <Box width={'100%'}>
                        <Grid
                          key={index}
                          container
                          boxShadow={
                            ' rgba(13, 108, 232, 0.06) 0px 0px 0px 1px, rgba(0, 74, 235, 0.03) 0px 1px 1px 1px,  rgba(23, 75, 186, 0.03) 0px 24px 24px 8px;'
                          }
                          p={1}
                          bgcolor={blueGrey[50]}
                        >
                          <Grid item xs={12} pb={2}>
                            <Box
                              display={'flex'}
                              justifyContent={'space-between'}
                              bgcolor={grey[50]}
                            >
                              <Box>
                                {search?.name ? (
                                  <Box display={'flex'} p={0.5}>
                                    <Typography variant={'h6'} color={grey[900]} fontWeight={700}>
                                      Search Name:
                                    </Typography>
                                    <Typography variant={'h6'} color={grey[700]} ml={2}>
                                      {search.name}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box display={'flex'} p={0.5}>
                                    <Typography variant={'h6'} color={grey[900]} fontWeight={700}>
                                      Search Name:
                                    </Typography>
                                    <Typography variant={'h6'} color={brown[200]} ml={2}>
                                      *** No Name
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              <Box display={'flex'} alignItems={'center'}>
                                <Box>
                                  {search?.keywords?.length ?? 0 > 0 ? (
                                    <>
                                      <Button
                                        size={'small'}
                                        variant={'text'}
                                        onClick={handleOpenPopper(
                                          `popper_keywords_${index}`,
                                          'bottom-end'
                                        )}
                                        sx={{mr: 2}}
                                      >
                                        <Badge
                                          sx={{
                                            '.MuiBadge-badge': {
                                              color: red[600],
                                              border: `1px solid ${grey[400]}`,
                                              backgroundColor: 'white',
                                              mr: -0.5,
                                            },
                                            '& span': {zIndex: 0, mr: -1, ml: 1, pl: 0.5, pr: 0.5},
                                          }}
                                          badgeContent={search?.keywords?.length}
                                        >
                                          Keyword Filters
                                        </Badge>
                                      </Button>
                                      <Popper
                                        open={
                                          showPopper
                                            ? showPopper.popper_id === `popper_keywords_${index}`
                                            : false
                                        }
                                        anchorEl={anchorEl}
                                        placement={placement}
                                        sx={{width: 'fit-content', minWidth: '300px'}}
                                        transition
                                      >
                                        {({TransitionProps}) => (
                                          <Fade {...TransitionProps} timeout={350}>
                                            <Paper sx={{p: 1}}>
                                              <Box
                                                display={'flex'}
                                                justifyContent={'space-between'}
                                                mb={2}
                                              >
                                                <Typography>Keyword Filters</Typography>
                                                <Button
                                                  variant='contained'
                                                  size={'small'}
                                                  onClick={handleOpenPopper(
                                                    `popper_${index}`,
                                                    'bottom-end'
                                                  )}
                                                  color={'inherit'}
                                                  disabled={false}
                                                >
                                                  Close
                                                </Button>
                                              </Box>
                                              <Divider sx={{mb: 1}} />
                                              <Box
                                                display={'flex'}
                                                flexDirection={'column'}
                                                gap={1}
                                              >
                                                <Stack
                                                  direction={'column'}
                                                  spacing={1}
                                                  flexWrap={'wrap'}
                                                >
                                                  {search?.keywords?.map((kw, idx) => {
                                                    return (
                                                      <Box display={'inline-flex'} key={idx}>
                                                        <Chip
                                                          key={idx}
                                                          onDelete={() =>
                                                            handleDeleteKeyword(search, kw)
                                                          }
                                                          label={kw}
                                                          sx={{
                                                            mt: 0.5,
                                                            mb: 0.5,
                                                            bgcolor: 'default',
                                                            '& .MuiChip-deleteIcon': {
                                                              color: indigo[300],
                                                              fontSize: '1.3rem',
                                                            },
                                                            '& .MuiChip-label': {
                                                              fontSize: '1rem',
                                                            },
                                                          }}
                                                        />
                                                      </Box>
                                                    )
                                                  })}
                                                </Stack>
                                              </Box>
                                            </Paper>
                                          </Fade>
                                        )}
                                      </Popper>
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </Box>

                                <Box>
                                  {search?.concepts?.filter((f) => f.__lock === 'exclude')
                                    ?.length ?? 0 > 0 ? (
                                    <>
                                      <Button
                                        size={'small'}
                                        variant={'text'}
                                        onClick={handleOpenPopper(`popper_${index}`, 'bottom-end')}
                                        sx={{mr: 2}}
                                      >
                                        <Badge
                                          sx={{
                                            '.MuiBadge-badge': {
                                              color: red[600],
                                              border: `1px solid ${grey[400]}`,
                                              backgroundColor: 'white',
                                              mr: -0.5,
                                            },
                                            '& span': {zIndex: 0, mr: -1, ml: 1, pl: 0.5, pr: 0.5},
                                          }}
                                          badgeContent={
                                            search?.concepts?.filter((f) => f.__lock === 'exclude')
                                              ?.length
                                          }
                                        >
                                          Ignored Concepts
                                        </Badge>
                                      </Button>
                                      <Popper
                                        open={
                                          showPopper
                                            ? showPopper.popper_id === `popper_${index}`
                                            : false
                                        }
                                        anchorEl={anchorEl}
                                        placement={placement}
                                        sx={{width: 'fit-content'}}
                                        transition
                                      >
                                        {({TransitionProps}) => (
                                          <Fade {...TransitionProps} timeout={350}>
                                            <Paper sx={{p: 1}}>
                                              <Box
                                                display={'flex'}
                                                justifyContent={'space-between'}
                                                mb={2}
                                              >
                                                <Typography>Ignored Concepts</Typography>
                                                <Button
                                                  variant='contained'
                                                  size={'small'}
                                                  onClick={handleOpenPopper(
                                                    `popper_${index}`,
                                                    'bottom-end'
                                                  )}
                                                  color={'inherit'}
                                                  disabled={false}
                                                >
                                                  Close
                                                </Button>
                                              </Box>
                                              <Divider sx={{mb: 1}} />
                                              <Box
                                                display={'flex'}
                                                flexDirection={'column'}
                                                gap={1}
                                              >
                                                {/* {search?.concepts
                                                ?.filter((f) => f.__lock === 'exclude')
                                                ?.map((c, i) => {
                                                  return (
                                                    <SmallConceptChip
                                                      key={`${i}_cChip`}
                                                      concept={c}
                                                      ignore={true}
                                                    />
                                                  )
                                                })} */}
                                                <DragableConceptsListNotifications
                                                  search={search}
                                                  ignoredOnly={true}
                                                  stackVertical={true}
                                                />
                                              </Box>
                                            </Paper>
                                          </Fade>
                                        )}
                                      </Popper>
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </Box>
                                <Button
                                  variant={'text'}
                                  onClick={() =>
                                    search && handleEditSearch && handleEditSearch(search, true)
                                  }
                                  disableRipple
                                >
                                  <EditIcon />
                                  Edit
                                </Button>
                                <Button
                                  variant={'text'}
                                  onClick={() => setPauseSearch(search)}
                                  disableRipple
                                >
                                  {search.is_paused ? (
                                    <>
                                      <PlayCircleOutlineIcon />
                                      Start
                                    </>
                                  ) : (
                                    <>
                                      <StopCircleIcon />
                                      Pause
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant={'text'}
                                  size={'small'}
                                  color={'error'}
                                  onClick={() => setDeleteSearch(search)}
                                >
                                  <DeleteIcon />
                                </Button>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={5} md={4} lg={4}>
                            <Box display={'flex'} alignItems={'center'} gap={1} p={1}>
                              {/* <Tooltip title={'View search criteria'}>
                              <Link
                                onClick={() => handleNavToSearch(search)}
                                to={`/discovery/${
                                  search.id
                                }?scrollPosition=${currScrollPosition}&fromSearchDirectory=${
                                  searchstring ? searchstring : 'tru'
                                }`}
                              >
                                <VisibilityIcon sx={{fontSize: '2em'}} />
                              </Link>
                            </Tooltip> */}

                              <ShowMoreText
                                variant={'body1'}
                                fontSize={'1.2em'}
                                maxLen={200}
                                text={search?.search_text ?? ''}
                                emptyCondition={'no description'}
                                moreString='...Show More'
                                lessString='...Show Less'
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={7} md={8} lg={8}>
                            <Box display={'flex'} alignItems={'baseline'} gap={1} p={1}>
                              <Box display={'flex'} justifyContent={'space-between'} ml={1} mr={1}>
                                <ShowMoreConceptsNotify
                                  search={search}
                                  concepts={
                                    search?.concepts?.filter((f) => f.__lock != 'exclude') ?? []
                                  }
                                  maxCons={6}
                                  moreString='...Show More Concepts'
                                  lessString='...Show Less Concepts'
                                />
                              </Box>
                            </Box>
                          </Grid>
                          {/* <Grid item xs={2} sm={2} md={2} lg={2}>
                          <CreatedUpdatedTile
                            createdUserId={search?.created?.by?.id ?? ''}
                            updatedUserId={search?.updated?.by?.id ?? ''}
                            createdDate={search?.created?.at ?? ''}
                            updatedDate={search?.updated?.at ?? ''}
                          />
                        </Grid> */}
                        </Grid>
                      </Box>
                    </Box>
                  </Box>
                  <Box display={'flex'} gap={2} justifyContent={'flex-end'} mr={1}>
                    {search?.notify_entities?.length ? (
                      search?.notify_entities?.map((ent, i) => {
                        return (
                          <Chip
                            key={`ent_${i}`}
                            label={
                              <Box
                                display={'flex'}
                                alignContent={'center'}
                                justifyItems={'center'}
                                alignItems={'center'}
                                justifyContent={'center'}
                              >
                                <Box component={'span'} fontWeight={500} fontSize={'.9rem'}>
                                  {ent}
                                </Box>
                                {/* for here just called the _data to get the is_metadata field, need to refactor types when get a chance */}
                                {/* <Box component={'span'} m={1} fontWeight={500} fontSize={'1.1rem'}>
                                  0
                                </Box> */}
                              </Box>
                            }
                            sx={{
                              background: blue[50],
                              color: 'black',
                              borderRadius: 0,

                              boxShadow:
                                'rgba(50, 50, 105, 0.15) 0px 2px 5px 0px, rgba(0, 0, 0, 0.05) 0px 1px 1px 0px',
                            }}
                            variant={'filled'}
                            size='small'
                          />
                        )
                      })
                    ) : (
                      <></>
                    )}
                  </Box>
                </Box>
              )
            })}
          </>
        )}

        {searchResults && searchResults.length === 0 && !loading && (
          <EmptyStateCard
            title={'No Searches Yet'}
            subTitle={'Your notification searches will show up here'}
            p={3}
            m={3}
            img={'wind'}
            size={'20%'}
          />
        )}
      </Box>
      {deleteSearch && (
        <ConfirmationDialog
          show={Boolean(deleteSearch)}
          setShow={() => setDeleteSearch(undefined)}
          callback={() => handleDeleteSearchFromList(deleteSearch?.id ?? '')}
          confirmText={
            <Box>
              <Typography variant={'h6'} color={grey[900]}>
                {`Are you sure you want to delete this notification search? Deleting this search will result in stopping any notifications you may be currently receiving based on this search.`}
              </Typography>

              <br />
              <Typography variant={'body1'} fontWeight={700}>
                Search Name:
              </Typography>
              <Typography variant={'body2'} color={grey[700]}>
                {deleteSearch.name}
              </Typography>

              <Typography variant={'body1'} fontWeight={700}>
                Search Text:
              </Typography>
              <Box color={grey[700]}>
                <ShowMoreText
                  variant={'body2'}
                  inheritColor={true}
                  maxLen={50}
                  text={deleteSearch?.search_text ?? ''}
                  emptyCondition={'no search text'}
                  moreString='...Show More'
                  lessString='...Show Less'
                />
              </Box>
            </Box>
          }
        />
      )}
      {pauseSearch && (
        <ConfirmationDialog
          show={Boolean(pauseSearch)}
          setShow={() => setPauseSearch(undefined)}
          callback={() => handlePauseSearchFromList(pauseSearch)}
          confirmText={
            <Box>
              <Typography variant={'h6'} color={grey[900]}>
                {`Are you sure you want to ${
                  pauseSearch.is_paused ? 'start' : 'pause'
                } this notification search? ${
                  pauseSearch.is_paused
                    ? 'Starting this search willl enable notifications.'
                    : 'Pausing this search will temporarily disable notifications for this search.'
                }`}
              </Typography>

              <br />
              <Typography variant={'body1'} fontWeight={700}>
                Search Name:
              </Typography>
              <Typography variant={'body2'} color={grey[700]}>
                {pauseSearch.name}
              </Typography>

              <Typography variant={'body1'} fontWeight={700}>
                Search Text:
              </Typography>
              <Box color={grey[700]}>
                <ShowMoreText
                  variant={'body2'}
                  inheritColor={true}
                  maxLen={50}
                  text={pauseSearch?.search_text ?? ''}
                  emptyCondition={'no search text'}
                  moreString='...Show More'
                  lessString='...Show Less'
                />
              </Box>
            </Box>
          }
        />
      )}
    </Box>
  )
}
